/**
 * كتالوج المزايا الثابتة.
 *
 * ما يتكرّر في المرتّب كلَّ شهر بلا أن يُكتب مرّةً بعد مرّة: بدلُ مواصلات،
 * وتأمينٌ طبّيٌّ تُخصم حصّتُه، واستقطاعُ قرض.
 *
 * ## ولماذا لا تكفي بنودُ المرتّب
 *
 * بندُ المرتّب واقعةٌ في شهرٍ بعينه: مكافأةٌ صُرفت في مارس. والبدلُ التزامٌ
 * قائمٌ يتكرّر حتى يُوقَف — وكتابتُه كلَّ شهرٍ بيدٍ لثلاثين موظّفًا هي
 * بالضبط ما يُنسى في شهرٍ فيشتكي ثلاثون.
 *
 * ## والخطّةُ غيرُ الاشتراك فيها
 *
 * خطّةٌ واحدةٌ يشترك فيها ثلاثون بمبالغَ قد تختلف — بدلُ المواصلات يختلف
 * بالمسافة، وحصّةُ التأمين تختلف بالفئة. وخلطُهما يجعل تعديلَ مبلغِ
 * الخطّة تعديلَ ثلاثين صفًّا بيد.
 */
import type { Labelled } from './common.js';

/* ==========================================================================
   أشكالُ المزايا
   ========================================================================== */

export const BENEFIT_KINDS = ['ALLOWANCE', 'MEDICAL', 'DEDUCTION'] as const;
export type BenefitKind = (typeof BENEFIT_KINDS)[number];

export interface BenefitKindDef extends Labelled<BenefitKind> {
  description: string;
  /** أثرُها في مرتّب الموظّف: زيادةٌ أو خصمٌ أو لا شيء. */
  sign: 1 | -1;
  /** هل لها حصّةٌ يدفعها الموظّف غيرُ ما تدفعه الشركة. */
  hasShare: boolean;
  /** ما يُسمّى به المبلغُ في النموذج. */
  amountLabel: string;
}

export const BENEFIT_KIND_DEFS: readonly BenefitKindDef[] = [
  {
    key: 'ALLOWANCE',
    label: 'بدل',
    kind: 'success',
    description: 'يُضاف إلى مرتّبه كلَّ شهر: مواصلاتٌ أو سكنٌ أو هاتف.',
    sign: 1,
    hasShare: false,
    amountLabel: 'قيمة البدل الشهريّة',
  },
  {
    key: 'MEDICAL',
    label: 'تأمين طبّيّ',
    kind: 'info',
    description:
      'الشركةُ تدفع للجهة المقدِّمة، وحصّةُ الموظّف — إن كانت — تُخصم من مرتّبه.',
    sign: -1,
    hasShare: true,
    amountLabel: 'ما تدفعه الشركة شهريًّا',
  },
  {
    key: 'DEDUCTION',
    label: 'استقطاع ثابت',
    kind: 'warning',
    description: 'يُخصم كلَّ شهر: قسطُ قرضٍ أو اشتراكُ ناد.',
    sign: -1,
    hasShare: false,
    amountLabel: 'ما يُخصم شهريًّا',
  },
] as const;

export const BENEFIT_KIND_MAP = Object.fromEntries(
  BENEFIT_KIND_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<BenefitKind, BenefitKindDef>>;

/* ==========================================================================
   ما يقع في مرتّب شهرٍ بعينه
   ========================================================================== */

export interface BenefitLine {
  /** اسمُ الخطّة كما يُقرأ في القسيمة. */
  label: string;
  kind: BenefitKind;
  /** ما يُضاف إلى مرتّبه أو يُخصم منه — موجبٌ دائمًا، والإشارةُ في النوع. */
  amount: number;
  /** ما تدفعه الشركةُ عنه — يُقرأ في تقرير الكلفة لا في قسيمته. */
  employerCost: number;
}

/**
 * هل كان الاشتراكُ حيًّا في شهرٍ بعينه.
 *
 * والمقارنةُ بالشهر لا باليوم: من اشترك في العشرين من مارس يُصرف له بدلُ
 * مارس كاملًا — البدلاتُ شهريّةٌ لا يوميّة، وتجزئتُها بالأيّام حسابٌ لم
 * تطلبه شركةٌ ويُنتج كسورًا في كلّ قسيمة.
 */
export function liveInMonth(
  startsOn: string,
  endsOn: string | null,
  periodStart: string,
): boolean {
  const month = periodStart.slice(0, 7);
  if (startsOn.slice(0, 7) > month) return false;
  if (endsOn && endsOn.slice(0, 7) < month) return false;
  return true;
}

/**
 * سطرُ الميزة في قسيمة شهر.
 *
 * حصّةُ الموظّف هي ما يُخصم منه في التأمين — لا ما تدفعه الشركة. وخلطُهما
 * يجعل قسيمةً تخصم من الرجل ما دفعته عنه شركتُه.
 */
export function benefitLine(input: {
  name: string;
  kind: BenefitKind;
  amount: number;
  employeeShare: number;
}): BenefitLine | null {
  const def = BENEFIT_KIND_MAP[input.kind];

  if (input.kind === 'MEDICAL') {
    return {
      label: input.name,
      kind: input.kind,
      amount: round2(input.employeeShare),
      employerCost: round2(input.amount),
    };
  }

  return {
    label: input.name,
    kind: input.kind,
    amount: round2(input.amount),
    /* البدلُ كلُّه كلفةٌ على الشركة، والاستقطاعُ لا يكلّفها شيئًا. */
    employerCost: def.sign === 1 ? round2(input.amount) : 0,
  };
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}
